import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [isAdult, setIsAdult] = useState(false);
  const [emailMatches, setEmailMatches] = useState(true);
  const [emailMessages, setEmailMessages] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isAdult) {
      setError(t("auth.errors.mustBeAdult"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.errors.minPassword"));
      return;
    }
    setLoading(true);
    const res = await register({
      email,
      password,
      is_adult: true,
      language: lang,
      country: country || null,
      city: city || null,
      referral_code: new URLSearchParams(location.search).get("ref") || null,
      notification_preferences: { email_matches: emailMatches, email_messages: emailMessages },
    });
    setLoading(false);
    if (res.ok) {
      navigate("/app/discover", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthLayout title={t("auth.register.title")} subtitle={t("auth.register.subtitle")} testId="register-page">
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.email")}
          </label>
          <input
            type="email"
            required
            className="nvl-input mt-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.password")}
          </label>
          <input
            type="password"
            required
            minLength={8}
            className="nvl-input mt-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input"
            autoComplete="new-password"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("auth.register.country")}
            </label>
            <input
              type="text"
              className="nvl-input mt-2"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="register-country-input"
              autoComplete="country-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("auth.register.city")}
            </label>
            <input
              type="text"
              className="nvl-input mt-2"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="register-city-input"
              autoComplete="address-level2"
            />
          </div>
        </div>

        <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="register-adult-label">
          <input
            type="checkbox"
            checked={isAdult}
            onChange={(e) => setIsAdult(e.target.checked)}
            className="mt-1 h-4 w-4"
            data-testid="register-adult-checkbox"
            required
          />
          <span>{t("auth.register.adult")}</span>
        </label>

        <div className="nvl-card p-4 text-sm" style={{ borderColor: "hsl(var(--nvl-border))" }} data-testid="register-email-notice">
          <p className="font-medium">{lang === "it" ? "Notifiche email" : "Email notifications"}</p>
          <p className="mt-1 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {lang === "it"
              ? "Riceverai email per nuovi match e messaggi non letti. Puoi scegliere ora oppure modificarle in qualsiasi momento nelle Impostazioni."
              : "You will receive emails for new matches and unread messages. You can choose now or change them at any time in Settings."}
          </p>
          <label className="mt-3 flex items-center gap-2 text-xs">
            <input type="checkbox" checked={emailMatches} onChange={(e) => setEmailMatches(e.target.checked)} data-testid="register-email-matches" />
            {lang === "it" ? "Avvisami dei nuovi match" : "Notify me about new matches"}
          </label>
          <label className="mt-2 flex items-center gap-2 text-xs">
            <input type="checkbox" checked={emailMessages} onChange={(e) => setEmailMessages(e.target.checked)} data-testid="register-email-messages" />
            {lang === "it" ? "Avvisami dei messaggi non letti" : "Notify me about unread messages"}
          </label>
        </div>

        {error ? (
          <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="register-error">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="nvl-btn nvl-btn-primary w-full disabled:opacity-60"
          data-testid="register-submit-button"
        >
          {loading ? t("common.loading") : t("auth.register.submit")}
        </button>

        <p className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("auth.register.terms")}{" "}
          <a href="/legal/terms" target="_blank" rel="noreferrer" className="underline"
             data-testid="register-legal-terms">{t("app.legal.termsTitle")}</a>
          {" · "}
          <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline"
             data-testid="register-legal-privacy">{t("app.legal.privacyTitle")}</a>
        </p>

        <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("auth.register.have")}{" "}
          <Link to="/login" data-testid="register-login-link" className="font-medium underline">
            {t("auth.register.login")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
